/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DynamicDestinationsPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for Dynamic Destinations management with list view.

 🔧 Notes:
    - Refactored from monolithic 403-line file
    - Uses hooks for state management
    - Delegates data fetching to useDynamicDestinationList

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Share2, Copy } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useDataGrid } from '../../hooks/useDataGrid';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { useDynamicDestinationList } from './hooks/useDynamicDestinationList';
import {
  deleteDynamicDestinationById,
  navigateToDynamicDestinationDuplicate,
} from './dynamicDestinationsActions';
import { DynamicDestination } from './types';

export default function DynamicDestinationsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    entry: DynamicDestination | null;
  }>({
    isOpen: false,
    entry: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const pageTitle = useMemo(() => t('dynamicDestination.title'), [t]);

  useEffect(() => {
    document.title = `${pageTitle} | Ring2All`;
  }, [pageTitle]);

  const { fetchData } = useDynamicDestinationList({
    domainId: selectedDomain?.id,
  });

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: t('dynamicDestination.fields.name.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DynamicDestination) => (
          <div className="space-y-1">
            <div className="font-medium text-foreground">{item.name}</div>
            {item.description ? (
              <div className="max-w-md truncate text-sm text-muted-foreground">
                {item.description}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'sourceType',
        label: t('dynamicDestination.fields.sourceType.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DynamicDestination) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.sourceType === 'DB'
              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
              }`}
          >
            {t(`dynamicDestination.sourceTypes.${item.sourceType}`)}
          </span>
        ),
      },
      {
        key: 'matchField',
        label: t('dynamicDestination.fields.matchField.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DynamicDestination) => (
          <span className="text-sm text-foreground">{item.matchField}</span>
        ),
      },
      {
        key: 'defaultDestination',
        label: t('dynamicDestination.fields.defaultDestination.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DynamicDestination) => (
          <span className="text-sm text-muted-foreground">{item.defaultDestination}</span>
        ),
      },
      {
        key: 'optionsCount',
        label: t('dynamicDestination.fields.optionsCount.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DynamicDestination) => (
          <span className="text-sm text-muted-foreground">{item.optionsCount}</span>
        ),
      },
      {
        key: 'active',
        label: t('dynamicDestination.fields.active.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DynamicDestination) => (
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${item.active
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
              }`}
          >
            {item.active ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false,
      },
    ],
    [t],
  );

  const {
    data,
    loading,
    columns: configuredColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: columns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'dynamicDestination',
  });

  // Refresh data when domain changes
  useEffect(() => {
    if (selectedDomain?.id !== undefined) {
      refresh();
    }
  }, [selectedDomain?.id]);

  const handleCreate = () => {
    navigate('/pbx/applications/dynamic-destination/new');
  };

  const handleEdit = useCallback((entry: DynamicDestination) => {
    navigate(`/pbx/applications/dynamic-destination/${entry.uuid || entry.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((entry: DynamicDestination) => {
    navigateToDynamicDestinationDuplicate(navigate, String(entry.uuid || entry.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((entry: DynamicDestination) => {
    setDeleteModal({ isOpen: true, entry });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.entry) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteDynamicDestinationById(String(deleteModal.entry.uuid || deleteModal.entry.id));

      showSuccess(t('dynamicDestination.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, entry: null });
      refresh();
    } catch (error) {
      showError(error?.message || t('dynamicDestination.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.entry, refresh, showError, showSuccess, t]);

  const renderRowActions = useCallback(
    (entry: DynamicDestination) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(entry)}
            aria-label={t('common.edit')}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(entry)}
            aria-label={t('common.duplicate')}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(entry)}
            aria-label={t('common.delete')}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={configuredColumns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={pageTitle}
          titleIcon={<Share2 className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          searchPlaceholder={t('dynamicDestination.searchPlaceholder')}
          renderActions={renderRowActions}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, entry: null })}
        onConfirm={handleDeleteConfirm}
        title={t('dynamicDestination.messages.deleteTitle')}
        message={t('dynamicDestination.messages.confirmDelete', {
          name: deleteModal.entry?.name ?? '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
